<script setup>
defineProps({
  color: {
    type: String,
    default: "success",
  },
  size: {
    type: String,
    default: "md",
  },
  variant: {
    type: String,
    default: "fill",
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
});
const getClasses = (variant, color, size, fullWidth, active) => {
  let colorValue, sizeValue, fullWidthValue, activeValue;

  // Export style override
  if (variant === "export") {
    return `btn-export ${size ? `btn-${size}` : ''} ${fullWidth ? 'w-100' : ''} ${active ? 'active' : ''}`;
  }

  // Setting the button variant and color
  if (variant === "gradient") {
    colorValue = `bg-gradient-${color}`;
  } else if (variant === "outline") {
    colorValue = `btn-outline-${color}`;
  } else {
    colorValue = `btn-${color}`;
  }

  sizeValue = size ? `btn-${size}` : null;

  fullWidthValue = fullWidth ? `w-100` : null;

  activeValue = active ? `active` : null;

  return `${colorValue} ${sizeValue} ${fullWidthValue} ${activeValue}`;
};
</script>
<template>
  <button class="btn mb-0" :class="getClasses(variant, color, size, fullWidth, active)">
    <template v-if="variant === 'export'">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
        style="margin-right: 0px;">
        <path d="M12 16.0001L12 3.00006M12 16.0001L8.5 12.5001M12 16.0001L15.5 12.5001M4 21.0001H20" stroke="#4B5563"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </template>
    <slot />
  </button>
</template>

<style scoped>
/* --- Base Export Button Style (Matches Image) --- */
.btn-export {
  /* Flexbox for icon + text alignment */
  display: inline-flex; /* Use inline-flex to keep button flow */
  align-items: center;   /* Vertically center content */
  justify-content: center; /* Horizontally center content */
  gap: 8px; /* Space between icon and text (adjust as needed) */

  /* Appearance */
  background-color: white !important;
  border: 1px solid #D1D5DB !important; /* Tailwind gray-300 - light grey */
  border-radius: 8px; /* More rounded corners */
  color: #374151 !important; /* Tailwind gray-700 - dark grey text */

  /* Sizing & Spacing (matches common input height) */
  height: 40px;
  padding: 0 16px; /* Horizontal padding */
  box-sizing: border-box; /* Include border/padding in height */

  /* Font */
  font-size: 14px; /* Common UI font size */
  font-weight: 500; /* Medium weight */

  /* Behavior */
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05); /* Subtle shadow */
  white-space: nowrap; /* Prevent text wrapping */
}

.btn-export:hover {
  background-color: #F9FAFB; /* Tailwind gray-50 - very light hover */
  border-color: #9CA3AF; /* Tailwind gray-400 - slightly darker border */
}

/* Active state style */
.btn-export.active {
   background-color: #F3F4F6; /* Tailwind gray-100 */
   box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
}

/* --- Optional Size Modifiers for Export Variant --- */
/* Example for btn-sm */
.btn-export.btn-sm {
    height: 32px;
    padding: 0 12px;
    font-size: 12px;
    gap: 6px;
    border-radius: 6px; /* Slightly less rounded for smaller */
}

/* Example for btn-lg */
.btn-export.btn-lg {
    height: 48px;
    padding: 0 24px;
    font-size: 16px;
    gap: 10px;
    border-radius: 10px; /* Slightly more rounded for larger */
}

/* --- Styling Slotted Content (e.g., Icons) --- */
/* Use :deep() to style elements passed via slot if needed */
.btn-export :deep(i),
.btn-export :deep(svg) {
  /* Ensure icon size is appropriate */
  /* Example: */
   width: 1em;
   height: 1em;
   /* You might want icons to inherit text color or have a specific one */
   /* color: inherit; */
}

/* --- Fallback/Existing Styles (Ensure .btn is defined elsewhere) --- */
/* Make sure you have base .btn styles defined globally or import them */
/* .btn { ... } */
/* .mb-0 { margin-bottom: 0 !important; } */
/* .w-100 { width: 100% !important; } */

</style>